import { getDbSafe } from './firebaseConfig';
import {
    doc,
    collection,
    runTransaction,
    serverTimestamp,
    getDoc,
    getDocs,
    setDoc,
    updateDoc,
    addDoc,
    deleteField,
    arrayUnion,
    arrayRemove,
    increment,
    onSnapshot,
    query,
    where,
    orderBy,
    limit,
    Timestamp,
} from 'firebase/firestore';
import type { Room, RoomJoinRequest, RoomMember } from '../types';
import { getUserAvatar } from '../utils/userUtils';
import {
    ROOM_MAX_MEMBERS,
    ROOM_MAX_MODS,
    canAcceptMember,
    canRequestToJoin,
    extendedExpiresAt,
    isValidDurationMinutes,
    isValidMuteMinutes,
    normalizeJoinPolicy,
    ROOM_QUESTION_MAX_LENGTH,
    ROOM_ANSWER_MAX_LENGTH,
    type RoomJoinPolicy,
    transitionForMemberCount,
    type RoomLifecycleState,
    type RoomPatch,
} from './roomLifecycle';

/**
 * Rooms: temporary, public, time-boxed group chats.
 *
 * Deliberately shaped like GovernanceService's tribe/forum functions — client
 * transactions plus Firestore rules, not callables — so the app has one
 * enforcement model rather than two. What is genuinely new is the lifecycle;
 * every transition decision is delegated to roomLifecycle.ts, which is pure and
 * unit-tested, so this file stays about writes.
 *
 * Two things are NOT enforceable from here and live elsewhere on purpose:
 *   • Purging an expired room needs recursiveDelete, which is Admin-SDK only —
 *     see sweepExpiredRooms in functions/rooms.js. Ending a room from the client
 *     sets status:'closed'; the sweep does the deletion.
 *   • A membership write on approval is done by the onRoomJoinRequestApproved
 *     trigger, matching how tribe join requests already work: the client flips
 *     the request status, the server performs the authoritative write.
 *
 * Timing note: transitions stamp client time (Date.now()). Skew shifts a
 * countdown by seconds at worst, and the server sweep — which uses server time —
 * is the authority on when a room actually dies.
 */

const ROOMS = 'rooms';
const ROOM_REPORTS = 'room_reports';

// ── Client-side speed bumps ─────────────────────────────────────────────────
// The same in-memory shape api.ts uses for sendMessage/createYap. This is NOT
// authoritative — it survives neither a reload nor a determined caller. The real
// caps (one hosted room, 15 members, mute bounds, one extension) are enforced in
// the transactions below and in firestore.rules.
const _roomRateLimit: Map<string, Record<string, number>> = new Map();

function _checkRoomRateLimit(uid: string, action: string, cooldownMs: number): void {
    const now = Date.now();
    const forUser = _roomRateLimit.get(uid) || {};
    const last = forUser[action] || 0;
    if (now - last < cooldownMs) {
        const wait = Math.ceil((cooldownMs - (now - last)) / 1000);
        throw new Error(`Slow down — try again in ${wait}s.`);
    }
    forUser[action] = now;
    _roomRateLimit.set(uid, forUser);
}

/** Firestore Timestamp | Date | number | null → epoch ms | null. */
export function toMillis(value: any): number | null {
    if (value == null) return null;
    if (typeof value === 'number') return value;
    if (typeof value?.toMillis === 'function') return value.toMillis();
    if (value instanceof Date) return value.getTime();
    const parsed = new Date(value).getTime();
    return Number.isNaN(parsed) ? null : parsed;
}

/** Room document → the plain, millisecond-based shape roomLifecycle reasons about. */
export function lifecycleStateOf(room: any): RoomLifecycleState {
    return {
        status: room?.status ?? 'lobby',
        memberCount: room?.memberCount ?? 0,
        durationMinutes: room?.durationMinutes ?? 60,
        extended: room?.extended === true,
        startedAt: toMillis(room?.startedAt),
        expiresAt: toMillis(room?.expiresAt),
        gracePeriodStartedAt: toMillis(room?.gracePeriodStartedAt),
    };
}

/** roomLifecycle's millisecond patch → a Firestore-writable patch. */
function patchToFirestore(patch: RoomPatch): Record<string, any> {
    const out: Record<string, any> = {};
    if (patch.status !== undefined) out.status = patch.status;
    if (patch.startedAt !== undefined) {
        out.startedAt = patch.startedAt == null ? null : Timestamp.fromMillis(patch.startedAt);
    }
    if (patch.expiresAt !== undefined) {
        out.expiresAt = patch.expiresAt == null ? null : Timestamp.fromMillis(patch.expiresAt);
    }
    if (patch.gracePeriodStartedAt !== undefined) {
        out.gracePeriodStartedAt = patch.gracePeriodStartedAt == null
            ? null
            : Timestamp.fromMillis(patch.gracePeriodStartedAt);
    }
    return out;
}

/**
 * The viewer's age bracket, resolved the same fail-closed way the rest of the
 * app does it: anything missing or unreadable reads as 'minor'.
 *
 * Rooms are live chat with strangers, so they inherit the bracket separation the
 * users collection already enforces — a minor's feed and an adult's feed are
 * disjoint, and neither can request to join the other's rooms.
 */
export async function ageGroupOf(db: any, userId: string): Promise<'minor' | 'adult'> {
    try {
        const snap = await getDoc(doc(db, 'users', userId));
        return snap.exists() && snap.data()?.ageGroup === 'adult' ? 'adult' : 'minor';
    } catch {
        return 'minor';
    }
}

async function profileOf(db: any, userId: string) {
    try {
        const snap = await getDoc(doc(db, 'users', userId));
        if (!snap.exists()) return { name: 'Someone', username: '', avatar: '' };
        const d = snap.data() as any;
        return {
            name: d.name || d.username || 'Someone',
            username: d.username || '',
            avatar: getUserAvatar(d),
        };
    } catch {
        return { name: 'Someone', username: '', avatar: '' };
    }
}

/**
 * Write a "joined"/"left" line into the room's own message stream.
 *
 * Kept as a message rather than derived from membership so it lands in the
 * conversation in the order it happened — a leave that shows up detached from
 * the moment it occurred tells you less than nothing.
 *
 * senderId is the person it is ABOUT, which is what lets the rules accept it:
 * room messages require senderId == request.auth.uid, and someone announcing
 * their own arrival or departure satisfies that without loosening anything. The
 * `type` field is what keeps it out of the normal bubble rendering.
 *
 * Best-effort: a failed system line must never fail the join or the leave it
 * describes.
 */
async function writeSystemMessage(
    db: any,
    roomId: string,
    userId: string,
    text: string,
    profile?: { name: string; avatar: string },
): Promise<void> {
    try {
        await addDoc(collection(db, ROOMS, roomId, 'messages'), {
            senderId: userId,
            type: 'system',
            text,
            // senderName/senderAvatar are carried even though the system line
            // does not render them. A client that predates the `type` check
            // falls through to the normal bubble path, and without these it
            // shows "Someone" over a "?" avatar. With them it degrades to an
            // ordinary-looking message from the right person, which is wrong but
            // not broken — and it costs two fields.
            senderName: profile?.name || '',
            senderAvatar: profile?.avatar || '',
            timestamp: serverTimestamp(),
        });
    } catch {
        /* the membership change itself already happened */
    }
}

/** Owner + mods. The audience for join requests and grace warnings. */
function staffOf(room: any): string[] {
    const mods: string[] = Array.isArray(room?.moderators) ? room.moderators : [];
    return Array.from(new Set([room?.ownerId, ...mods].filter(Boolean).map(String)));
}

/**
 * Drops anyone who has muted notifications for this specific room.
 *
 * The preference lives on the member document rather than the user document
 * because it is per-user-per-room: muting one room must not quieten another.
 * Reading it costs one document per recipient, and room notifications only ever
 * go to a handful of people (staff, or a single user), never the whole roster.
 *
 * Fails OPEN: if the membership read fails the notification is still sent. A
 * notification that should have been suppressed is a small annoyance; a missed
 * join request or closure warning is the host losing their room.
 */
async function dropMutedRecipients(userIds: string[], roomId?: string): Promise<string[]> {
    if (!roomId || userIds.length === 0) return userIds;
    try {
        const db = await getDbSafe();
        const checks = await Promise.all(userIds.map(async (uid) => {
            try {
                const snap = await getDoc(doc(db, ROOMS, roomId, 'members', uid));
                return snap.exists() && snap.data()?.notificationsMuted === true ? null : uid;
            } catch {
                return uid;
            }
        }));
        return checks.filter((uid): uid is string => uid !== null);
    } catch {
        return userIds;
    }
}

async function notify(userIds: string[], type: string, title: string, body: string, data: any, roomId?: string) {
    if (userIds.length === 0) return;
    try {
        const recipients = await dropMutedRecipients(userIds, roomId);
        if (recipients.length === 0) return;
        const { NotificationService } = await import('./api');
        await Promise.all(recipients.map(uid =>
            NotificationService.sendNotification(uid, type, title, body, data).catch(() => undefined)
        ));
    } catch (e) {
        console.error('[RoomService] notification dispatch failed:', e);
    }
}

/** Audit trail, same collection and shape LegionService writes for app-wide actions. */
async function writeModLog(
    db: any,
    actorId: string,
    targetId: string,
    actionType: string,
    reason: string,
    extra: Record<string, any> = {},
) {
    try {
        const [actor, target] = await Promise.all([profileOf(db, actorId), profileOf(db, targetId)]);
        await addDoc(collection(db, 'moderation_logs'), {
            actorId,
            actorUsername: actor.username || 'Unknown',
            targetId,
            targetUsername: target.username || 'Unknown',
            actionType,
            reason: reason || '',
            timestamp: serverTimestamp(),
            ...extra,
        });
    } catch (e) {
        // Never block a moderation action because its audit entry failed.
        console.error('[RoomService] moderation_logs write failed:', e);
    }
}

function assertStaff(room: any, actorId: string, action: string) {
    if (!staffOf(room).includes(String(actorId))) {
        throw new Error(`Only the host or a room mod can ${action}.`);
    }
}

function assertOwner(room: any, actorId: string, action: string) {
    if (String(room?.ownerId) !== String(actorId)) {
        throw new Error(`Only the host can ${action}.`);
    }
}

export const RoomService = {

    // ── Creation ────────────────────────────────────────────────────────────

    /**
     * Create a room in lobby.
     *
     * The one-hosted-room-at-a-time rule is enforced the way createTribe enforces
     * its owned-group cap: inside the transaction, against a pointer on the user
     * document (hostedRoomId). The pointer is re-read rather than trusted — a
     * room that has since closed leaves a stale pointer, and that must not lock
     * someone out of hosting forever.
     */
    async createRoom(
        userId: string,
        input: { title: string; rules?: string; durationMinutes: number; moderatorIds?: string[]; joinPolicy?: RoomJoinPolicy; joinQuestion?: string },
    ): Promise<string> {
        const title = (input.title || '').trim();
        if (title.length < 3) throw new Error('Give your room a title (at least 3 characters).');
        if (title.length > 80) throw new Error('Titles are limited to 80 characters.');
        const joinPolicy = normalizeJoinPolicy(input.joinPolicy);
        const joinQuestion = (input.joinQuestion || '').trim();
        if (joinPolicy === 'question') {
            if (!joinQuestion) throw new Error('Add the question people must answer to join.');
            if (joinQuestion.length > ROOM_QUESTION_MAX_LENGTH) {
                throw new Error(`Keep the question under ${ROOM_QUESTION_MAX_LENGTH} characters.`);
            }
        }

        if (!isValidDurationMinutes(input.durationMinutes)) {
            throw new Error('Rooms run between 1 and 24 hours.');
        }
        const mods = Array.from(new Set((input.moderatorIds || []).map(String))).filter(id => id !== String(userId));
        if (mods.length > ROOM_MAX_MODS) throw new Error(`A room can have at most ${ROOM_MAX_MODS} mods.`);

        _checkRoomRateLimit(userId, 'createRoom', 30_000);

        const db = await getDbSafe();
        const userRef = doc(db, 'users', userId);
        const roomRef = doc(collection(db, ROOMS));
        const memberRef = doc(db, ROOMS, roomRef.id, 'members', userId);
        const profile = await profileOf(db, userId);
        const ageGroup = await ageGroupOf(db, userId);

        await runTransaction(db, async (tx) => {
            const userSnap = await tx.get(userRef);
            if (!userSnap.exists()) throw new Error('User not found.');
            const userData = userSnap.data() as any;

            const existingId = userData.hostedRoomId;
            if (existingId) {
                const existingSnap = await tx.get(doc(db, ROOMS, String(existingId)));
                const stillOpen = existingSnap.exists() && existingSnap.data()?.status !== 'closed';
                if (stillOpen) {
                    throw new Error('You already have a room open. End it before starting another.');
                }
            }

            tx.set(roomRef, {
                ownerId: userId,
                ownerName: profile.name,
                title,
                rules: (input.rules || '').trim(),
                status: 'lobby',
                // Bracket travels with the room; the rules verify it against the
                // creator's own and refuse a mismatch.
                ageGroup,
                durationMinutes: input.durationMinutes,
                // How people get in. Absent on rooms made before this existed,
                // which normalizeJoinPolicy reads as 'approval' — their original
                // behaviour.
                joinPolicy,
                joinQuestion: joinPolicy === 'question' ? joinQuestion : '',
                extended: false,
                // The host counts toward the 3 needed to go live.
                members: [userId],
                memberCount: 1,
                moderators: mods,
                createdAt: serverTimestamp(),
                startedAt: null,
                expiresAt: null,
                gracePeriodStartedAt: null,
                endingSoonNotifiedAt: null,
                lastActivityAt: serverTimestamp(),
            });

            tx.set(memberRef, {
                userId,
                role: 'owner',
                status: 'active',
                joinedAt: serverTimestamp(),
                mutedUntil: null,
                warnings: [],
                userName: profile.name,
                userUsername: profile.username,
                userAvatar: profile.avatar,
            });

            tx.update(userRef, { hostedRoomId: roomRef.id });
        });

        return roomRef.id;
    },

    // ── Reads ───────────────────────────────────────────────────────────────

    /**
     * Live feed of every open room in the viewer's age bracket, newest first.
     *
     * The bracket filter is not cosmetic: the read rule requires it, so an
     * unfiltered query returns a permission error rather than a mixed feed.
     */
    subscribeToOpenRooms(viewerId: string, onRooms: (rooms: Room[]) => void, onError?: (e: any) => void) {
        let unsub: (() => void) | null = null;
        let cancelled = false;

        (async () => {
            const db = await getDbSafe();
            if (cancelled) return;
            const ageGroup = await ageGroupOf(db, viewerId);
            if (cancelled) return;
            const q = query(
                collection(db, ROOMS),
                where('ageGroup', '==', ageGroup),
                where('status', 'in', ['lobby', 'active', 'grace']),
                orderBy('createdAt', 'desc'),
                limit(60),
            );
            unsub = onSnapshot(
                q,
                (snap) => onRooms(snap.docs.map(d => ({ id: d.id, ...d.data() })) as Room[]),
                (e) => {
                    console.error('[RoomService] rooms feed subscription failed:', e);
                    onError?.(e);
                },
            );
        })();

        return () => { cancelled = true; unsub?.(); };
    },

    subscribeToRoom(roomId: string, onRoom: (room: Room | null) => void) {
        let unsub: (() => void) | null = null;
        let cancelled = false;
        (async () => {
            const db = await getDbSafe();
            if (cancelled) return;
            unsub = onSnapshot(doc(db, ROOMS, roomId), (snap) => {
                onRoom(snap.exists() ? ({ id: snap.id, ...snap.data() } as Room) : null);
            }, (e) => console.error('[RoomService] room subscription failed:', e));
        })();
        return () => { cancelled = true; unsub?.(); };
    },

    async getRoom(roomId: string): Promise<Room | null> {
        const db = await getDbSafe();
        const snap = await getDoc(doc(db, ROOMS, roomId));
        return snap.exists() ? ({ id: snap.id, ...snap.data() } as Room) : null;
    },

    async getRoomMembers(roomId: string): Promise<RoomMember[]> {
        const db = await getDbSafe();
        const snap = await getDocs(collection(db, ROOMS, roomId, 'members'));
        return snap.docs.map(d => ({ userId: d.id, ...d.data() })) as RoomMember[];
    },

    async getMyMembership(roomId: string, userId: string): Promise<RoomMember | null> {
        const db = await getDbSafe();
        const snap = await getDoc(doc(db, ROOMS, roomId, 'members', userId));
        return snap.exists() ? ({ userId, ...snap.data() } as RoomMember) : null;
    },

    /**
     * Mute or unmute this room's notifications, for this user only.
     *
     * Stored on the member document because the preference is per-user-per-room:
     * silencing one room must leave every other room alone, and the member doc is
     * the only place that pairing already exists. Leaving the room and coming back
     * keeps the setting, since the membership write on rejoin merges.
     */
    async setRoomNotificationsMuted(roomId: string, userId: string, muted: boolean): Promise<void> {
        const db = await getDbSafe();
        await updateDoc(doc(db, ROOMS, roomId, 'members', userId), {
            notificationsMuted: muted,
        });
    },

    async getMyJoinRequest(roomId: string, userId: string): Promise<RoomJoinRequest | null> {
        const db = await getDbSafe();
        const snap = await getDoc(doc(db, ROOMS, roomId, 'join_requests', userId));
        return snap.exists() ? ({ userId, ...snap.data() } as RoomJoinRequest) : null;
    },

    // ── Join requests ───────────────────────────────────────────────────────

    /**
     * Ask to join. Never auto-joins — the owner or a mod has to act.
     *
     * A kicked membership doc is what blocks a rejoin, so the check is a read of
     * that doc rather than a flag on the user. Mirrored in firestore.rules, since
     * this file is client code and cannot be the last word on it.
     */
    async createRoomJoinRequest(userId: string, roomId: string, answer?: string): Promise<void> {
        _checkRoomRateLimit(userId, `joinRequest:${roomId}`, 10_000);

        const db = await getDbSafe();
        const roomRef = doc(db, ROOMS, roomId);
        const memberRef = doc(db, ROOMS, roomId, 'members', userId);
        const requestRef = doc(db, ROOMS, roomId, 'join_requests', userId);

        const [roomSnap, memberSnap, existingSnap, meSnap] = await Promise.all([
            getDoc(roomRef), getDoc(memberRef), getDoc(requestRef), getDoc(doc(db, 'users', userId)),
        ]);

        if (!roomSnap.exists()) throw new Error('This room no longer exists.');
        const room = roomSnap.data() as any;
        if (room.status === 'closed') throw new Error('This room has ended.');

        // Blocked either way means they cannot share this room, and whoever is
        // already in it was here first. The real enforcement is server-side in
        // onRoomJoinRequestApproved; this only saves the user from queueing a
        // request that is going to be refused. Worded without naming anyone:
        // confirming who is in the room would defeat the point of the block.
        const me = meSnap.exists() ? (meSnap.data() as any) : null;
        const blockedEitherWay = new Set<string>([
            ...(me?.blockedUsers || []).map(String),
            ...(me?.blockedBy || []).map(String),
        ]);
        if ((room.members || []).some((id: any) => blockedEitherWay.has(String(id)))) {
            throw new Error('You cannot join this room.');
        }

        const membership = memberSnap.exists() ? (memberSnap.data() as any) : null;
        if (membership?.status === 'active') throw new Error("You're already in this room.");
        if (!canRequestToJoin(membership)) {
            throw new Error('You were removed from this room and cannot rejoin it.');
        }

        // Full rooms refuse NEW requests; requests already queued are left alone.
        if (!canAcceptMember({ status: room.status, memberCount: room.memberCount ?? 0 })) {
            throw new Error('This room is full.');
        }

        if (existingSnap.exists() && existingSnap.data()?.status === 'pending') {
            throw new Error('Your request is already waiting for the host.');
        }

        const policy = normalizeJoinPolicy(room.joinPolicy);
        const trimmedAnswer = (answer || '').trim();
        if (policy === 'question') {
            if (!trimmedAnswer) throw new Error('Answer the question to ask for a place.');
            if (trimmedAnswer.length > ROOM_ANSWER_MAX_LENGTH) {
                throw new Error(`Keep your answer under ${ROOM_ANSWER_MAX_LENGTH} characters.`);
            }
        }

        // Age bracket, checked HERE rather than left to the rules.
        //
        // roomIsSameAgeGroup() gates this in firestore.rules, and every other
        // reason a join can fail is pre-checked above with a message that says
        // why — this one was not, so a minor tapping an adult's room (or the
        // reverse) got a raw "Missing or insufficient permissions" with nothing
        // to act on. Reported against an OPEN room, where "anyone can join" makes
        // an unexplained refusal especially confusing.
        //
        // Fail-closed to 'minor' on an unreadable profile, matching ageGroupOf
        // and the rules themselves.
        const myBracket = await ageGroupOf(db, userId);
        const roomBracket = room.ageGroup === 'adult' ? 'adult' : 'minor';
        if (myBracket !== roomBracket) {
            throw new Error(
                roomBracket === 'adult'
                    ? 'This room is for adults only.'
                    : 'This room is for under-18s only.',
            );
        }

        const profile = await profileOf(db, userId);
        try {
        await setDoc(requestRef, {
            userId,
            requestedAt: serverTimestamp(),
            // An open room has no approval step: the request is written already
            // approved, so onRoomJoinRequestApproved mints the membership through
            // exactly the same path every other join uses. One door, not two —
            // and the block guard in that trigger still applies.
            status: policy === 'open' ? 'approved' : 'pending',
            // Only stored for a question room; an empty string elsewhere so the
            // requests panel never renders a blank answer block.
            answer: policy === 'question' ? trimmedAnswer : '',
            decidedBy: null,
            decidedAt: null,
            userName: profile.name,
            userUsername: profile.username,
            userAvatar: profile.avatar,
        });
        } catch (e: any) {
            // Anything the rules refuse that the checks above did not anticipate
            // reaches the user as Firestore's own wording, which tells them
            // nothing and looks like the app is broken. Everything expected is
            // caught above with a real explanation; this is the backstop.
            if (String(e?.code || '').includes('permission-denied')) {
                throw new Error('You are not able to join this room right now.');
            }
            throw e;
        }

        // An open room approves itself, so there is nothing for staff to action —
        // telling them someone "wants to join" would be a notification about a
        // decision that has already been made.
        if (policy === 'open') return;

        await notify(
            staffOf(room),
            'ROOM_JOIN_REQUEST',
            'New room request',
            `${profile.name} wants to join ${room.title || 'your room'}`,
            { roomId, roomTitle: room.title || '', requestUserId: userId, fromUserId: userId, fromUserName: profile.name },
            roomId,
        );
    },

    subscribeToRoomJoinRequests(roomId: string, onRequests: (reqs: RoomJoinRequest[]) => void) {
        let unsub: (() => void) | null = null;
        let cancelled = false;
        (async () => {
            const db = await getDbSafe();
            if (cancelled) return;
            const q = query(
                collection(db, ROOMS, roomId, 'join_requests'),
                where('status', '==', 'pending'),
            );
            unsub = onSnapshot(q, (snap) => {
                onRequests(snap.docs.map(d => ({ userId: d.id, ...d.data() })) as RoomJoinRequest[]);
            }, (e) => console.error('[RoomService] join requests subscription failed:', e));
        })();
        return () => { cancelled = true; unsub?.(); };
    },

    /**
     * Approve a request. Owner OR mod — unlike tribes, where approval is
     * owner-only.
     *
     * Only the request document is written here. onRoomJoinRequestApproved does
     * the membership write and the lobby→active transition server-side, the same
     * split tribes use, so a client cannot mint itself a membership by writing
     * the members subcollection directly.
     */
    async approveRoomJoinRequest(actorId: string, roomId: string, requestUserId: string): Promise<void> {
        const db = await getDbSafe();
        const roomSnap = await getDoc(doc(db, ROOMS, roomId));
        if (!roomSnap.exists()) throw new Error('This room no longer exists.');
        const room = roomSnap.data() as any;

        assertStaff(room, actorId, 'approve requests');
        if (room.status === 'closed') throw new Error('This room has ended.');

        // Capacity is checked HERE rather than when the request was made, so a
        // queue survives a full room and stays actionable if someone leaves.
        if (!canAcceptMember({ status: room.status, memberCount: room.memberCount ?? 0 })) {
            throw new Error(`This room is full (${ROOM_MAX_MEMBERS}/${ROOM_MAX_MEMBERS}). Someone has to leave first.`);
        }

        const memberSnap = await getDoc(doc(db, ROOMS, roomId, 'members', requestUserId));
        if (memberSnap.exists() && memberSnap.data()?.status === 'kicked') {
            throw new Error('This user was removed from the room and cannot be let back in.');
        }

        await updateDoc(doc(db, ROOMS, roomId, 'join_requests', requestUserId), {
            status: 'approved',
            decidedBy: actorId,
            decidedAt: serverTimestamp(),
        });

        await notify(
            [requestUserId],
            'ROOM_JOIN_APPROVED',
            "You're in",
            `Your request to join ${room.title || 'the room'} was approved`,
            { roomId, roomTitle: room.title || '' },
            roomId,
        );
    },

    async denyRoomJoinRequest(actorId: string, roomId: string, requestUserId: string): Promise<void> {
        const db = await getDbSafe();
        const roomSnap = await getDoc(doc(db, ROOMS, roomId));
        if (!roomSnap.exists()) throw new Error('This room no longer exists.');
        assertStaff(roomSnap.data(), actorId, 'deny requests');

        await updateDoc(doc(db, ROOMS, roomId, 'join_requests', requestUserId), {
            status: 'denied',
            decidedBy: actorId,
            decidedAt: serverTimestamp(),
        });
    },

    /**
     * Invite friends by dropping the room into their DMs.
     *
     * Reuses the groupInvite message card tribes and forums already send, typed
     * 'room'. It is an invitation to ASK, not a seat: the receiver lands on the
     * room and still has to request one, so this cannot be used to stuff a room
     * with people who never agreed to be there — and it cannot bypass the age
     * bracket, since the join-request rule checks that independently.
     *
     * Open to any member, not just the host: a room of three people that needs
     * five should not be bottlenecked on one person's contact list.
     */
    async inviteToRoom(roomId: string, inviterId: string, friendIds: string[]): Promise<number> {
        if (friendIds.length === 0) return 0;
        _checkRoomRateLimit(inviterId, 'inviteToRoom', 3_000);

        const db = await getDbSafe();
        const roomSnap = await getDoc(doc(db, ROOMS, roomId));
        if (!roomSnap.exists()) throw new Error('This room no longer exists.');
        const room = roomSnap.data() as any;
        if (room.status === 'closed') throw new Error('This room has ended.');

        const memberSnap = await getDoc(doc(db, ROOMS, roomId, 'members', inviterId));
        if (!memberSnap.exists() || memberSnap.data()?.status !== 'active') {
            throw new Error("You're not in this room.");
        }

        const { MessagingService } = await import('./api');
        const profile = await profileOf(db, inviterId);

        // A room invite must never cross the age bracket. The room itself is
        // already unreadable and unjoinable to the other side, but an invite
        // that arrives and then refuses to open is worse than one that was
        // never sent — and it tells a stranger a room exists.
        const roomBracket = room.ageGroup === 'adult' ? 'adult' : 'minor';
        const eligible: string[] = [];
        let skippedForAge = 0;
        for (const friendId of friendIds) {
            const theirs = await ageGroupOf(db, String(friendId));
            if (theirs === roomBracket) eligible.push(String(friendId));
            else skippedForAge++;
        }
        if (eligible.length === 0) {
            throw new Error(
                skippedForAge > 0
                    ? 'Rooms stay within your age group, so those people cannot be invited.'
                    : 'Nobody to invite.',
            );
        }

        let sent = 0;

        for (const friendId of eligible) {
            try {
                const chatId = await MessagingService.createChat(inviterId, String(friendId), true);
                await MessagingService.sendMessage(
                    chatId,
                    inviterId,
                    `${profile.name} invited you to a room: ${room.title}`,
                    {
                        groupInvite: {
                            id: String(roomId),
                            name: room.title || 'Room',
                            type: 'room',
                            bannerUrl: '',
                            isPublic: true,
                        },
                    },
                );
                sent++;
                // The messaging rules allow one message per second per sender.
                if (eligible.length > 1) await new Promise(r => setTimeout(r, 1100));
            } catch (e) {
                console.error(`[RoomService] invite to ${friendId} failed:`, e);
            }
        }

        return sent;
    },

    /**
     * Advertise the room in the world feed.
     *
     * A room is otherwise only discoverable by opening the Rooms tab, which is a
     * real problem for a room that needs three people before its countdown even
     * starts. The yap is an ordinary public yap carrying a roomShare reference,
     * so it flows through the existing feed, moderation and blocking paths with
     * no special casing.
     *
     * It does NOT create a way in: tapping the card opens the room, where a seat
     * still means a join request somebody approves. Open to any member for the
     * same reason invites are — a room three people short should not depend on
     * one person's reach.
     */
    async shareRoomToFeed(roomId: string, userId: string, note?: string): Promise<string> {
        _checkRoomRateLimit(userId, 'shareRoomToFeed', 60_000);

        const db = await getDbSafe();
        const roomSnap = await getDoc(doc(db, ROOMS, roomId));
        if (!roomSnap.exists()) throw new Error('This room no longer exists.');
        const room = roomSnap.data() as any;
        if (room.status === 'closed') throw new Error('This room has ended.');

        const memberSnap = await getDoc(doc(db, ROOMS, roomId, 'members', userId));
        if (!memberSnap.exists() || memberSnap.data()?.status !== 'active') {
            throw new Error("You're not in this room.");
        }

        const { YapService } = await import('./api');
        const text = (note || '').trim() || `Come join my room: ${room.title}`;
        const created = await YapService.createYap({
            content: text,
            visibility: 'public',
            // The bracket travels with the advert so the feed can filter it
            // without reading the room: a minor must not see an adult's room
            // advertised at all, not merely fail to open it.
            roomShare: {
                roomId,
                title: room.title || 'Room',
                ageGroup: room.ageGroup === 'adult' ? 'adult' : 'minor',
            },
        } as any);

        return String((created as any)?.id || '');
    },

    // ── Membership changes ──────────────────────────────────────────────────

    /**
     * Leave a room.
     *
     * The owner leaving closes the room outright — no mod is promoted. They have
     * to transfer ownership first if they want it to survive, which is the
     * opposite of tribes (where an owner simply cannot leave).
     */
    async leaveRoom(userId: string, roomId: string): Promise<{ closed: boolean }> {
        const db = await getDbSafe();
        const roomRef = doc(db, ROOMS, roomId);
        const memberRef = doc(db, ROOMS, roomId, 'members', userId);
        const userRef = doc(db, 'users', userId);

        // Announced before the transaction: room messages require an ACTIVE
        // membership, so this is the last moment the departing user is still
        // allowed to write it.
        {
            const leaving = await profileOf(db, userId);
            await writeSystemMessage(db, roomId, userId, `${leaving.name} left the room`, leaving);
        }

        const result = await runTransaction(db, async (tx) => {
            const roomSnap = await tx.get(roomRef);
            if (!roomSnap.exists()) return { closed: false, graceStarted: false, room: null as any };
            const room = roomSnap.data() as any;
            if (room.status === 'closed') return { closed: true, graceStarted: false, room };

            const memberSnap = await tx.get(memberRef);
            if (!memberSnap.exists() || memberSnap.data()?.status !== 'active') {
                return { closed: false, graceStarted: false, room };
            }

            if (String(room.ownerId) === String(userId)) {
                tx.update(roomRef, {
                    status: 'closed',
                    closedAt: serverTimestamp(),
                    members: arrayRemove(userId),
                    memberCount: increment(-1),
                });
                tx.update(memberRef, { status: 'left', leftAt: serverTimestamp() });
                tx.update(userRef, { hostedRoomId: deleteField() });
                return { closed: true, graceStarted: false, room };
            }

            const nextCount = Math.max(0, (room.memberCount ?? 1) - 1);
            const patch = transitionForMemberCount(lifecycleStateOf(room), nextCount, Date.now());

            tx.update(roomRef, {
                members: arrayRemove(userId),
                memberCount: nextCount,
                lastActivityAt: serverTimestamp(),
                ...patchToFirestore(patch),
            });
            tx.update(memberRef, { status: 'left', leftAt: serverTimestamp() });
            // Demote out of the mod list so the slot frees up.
            if (Array.isArray(room.moderators) && room.moderators.includes(userId)) {
                tx.update(roomRef, { moderators: arrayRemove(userId) });
            }

            return { closed: false, graceStarted: patch.status === 'grace', room };
        });

        if (result.graceStarted && result.room) {
            await notify(
                staffOf(result.room),
                'ROOM_GRACE_STARTED',
                'Room is running low',
                `${result.room.title || 'Your room'} dropped below 3 members. It closes in 30 minutes unless someone joins.`,
                { roomId, roomTitle: result.room.title || '' },
                roomId,
            );
        }

        return { closed: result.closed };
    },

    /** Owner-only, and only to a current mod. No cooldown — see the note below. */
    async transferRoomOwnership(roomId: string, currentOwnerId: string, newOwnerId: string): Promise<void> {
        const db = await getDbSafe();
        const roomRef = doc(db, ROOMS, roomId);

        await runTransaction(db, async (tx) => {
            const roomSnap = await tx.get(roomRef);
            if (!roomSnap.exists()) throw new Error('This room no longer exists.');
            const room = roomSnap.data() as any;

            assertOwner(room, currentOwnerId, 'transfer the room');
            if (room.status === 'closed') throw new Error('This room has ended.');
            if (String(newOwnerId) === String(currentOwnerId)) throw new Error('You already host this room.');

            const mods: string[] = Array.isArray(room.moderators) ? room.moderators : [];
            if (!mods.includes(String(newOwnerId))) {
                throw new Error('Ownership can only pass to a current mod.');
            }

            const newOwnerMemberRef = doc(db, ROOMS, roomId, 'members', newOwnerId);
            const newOwnerSnap = await tx.get(newOwnerMemberRef);
            if (!newOwnerSnap.exists() || newOwnerSnap.data()?.status !== 'active') {
                throw new Error('That mod is no longer in the room.');
            }

            // Deliberately no 24h cooldown. passOwnership uses one for tribes,
            // which are permanent; a room lives at most a day, so a cooldown
            // there would just be a way to strand a room with an absent host.
            tx.update(roomRef, {
                ownerId: newOwnerId,
                moderators: arrayRemove(newOwnerId),
                lastActivityAt: serverTimestamp(),
            });
            tx.update(newOwnerMemberRef, { role: 'owner' });
            tx.update(doc(db, ROOMS, roomId, 'members', currentOwnerId), { role: 'mod' });
            tx.update(doc(db, 'users', currentOwnerId), { hostedRoomId: deleteField() });
            tx.update(doc(db, 'users', newOwnerId), { hostedRoomId: roomId });
        });

        await notify(
            [newOwnerId],
            'ROOM_OWNERSHIP',
            "You're hosting",
            'Ownership of the room was transferred to you.',
            { roomId },
            roomId,
        );
    },

    /** Owner-only. Mods cannot appoint mods. Cap of 3 excludes the owner. */
    async setRoomModerator(roomId: string, ownerId: string, targetUserId: string, makeMod: boolean): Promise<void> {
        const db = await getDbSafe();
        const roomRef = doc(db, ROOMS, roomId);

        await runTransaction(db, async (tx) => {
            const roomSnap = await tx.get(roomRef);
            if (!roomSnap.exists()) throw new Error('This room no longer exists.');
            const room = roomSnap.data() as any;
            assertOwner(room, ownerId, 'change mods');

            const mods: string[] = Array.isArray(room.moderators) ? room.moderators : [];
            const memberRef = doc(db, ROOMS, roomId, 'members', targetUserId);

            if (makeMod) {
                if (mods.includes(String(targetUserId))) return;
                if (mods.length >= ROOM_MAX_MODS) throw new Error(`A room can have at most ${ROOM_MAX_MODS} mods.`);
                const memberSnap = await tx.get(memberRef);
                if (!memberSnap.exists() || memberSnap.data()?.status !== 'active') {
                    throw new Error('That person is not in the room.');
                }
                tx.update(roomRef, { moderators: arrayUnion(targetUserId) });
                tx.update(memberRef, { role: 'mod' });
            } else {
                tx.update(roomRef, { moderators: arrayRemove(targetUserId) });
                tx.update(memberRef, { role: 'member' });
            }
        });
    },

    // ── Moderation ──────────────────────────────────────────────────────────

    /**
     * Warn a member. Persisted on the membership document rather than shown once,
     * so the owner and every mod share one history when deciding to escalate.
     */
    async warnRoomMember(roomId: string, targetUserId: string, byUserId: string, reason: string): Promise<void> {
        const db = await getDbSafe();
        const roomSnap = await getDoc(doc(db, ROOMS, roomId));
        if (!roomSnap.exists()) throw new Error('This room no longer exists.');
        assertStaff(roomSnap.data(), byUserId, 'warn members');
        if (String(roomSnap.data()?.ownerId) === String(targetUserId)) throw new Error('The host cannot be warned.');

        const actor = await profileOf(db, byUserId);
        await updateDoc(doc(db, ROOMS, roomId, 'members', targetUserId), {
            warnings: arrayUnion({
                byUserId,
                byName: actor.name,
                reason: (reason || '').trim(),
                // arrayUnion cannot hold serverTimestamp(), so this is client time.
                at: Timestamp.now(),
            }),
        });

        await writeModLog(db, byUserId, targetUserId, 'room_warn', reason, { roomId });
    },

    /** Mute for a mod-chosen 5–60 minutes. Bounds are re-checked in rules. */
    async muteRoomMember(roomId: string, targetUserId: string, byUserId: string, durationMinutes: number): Promise<void> {
        if (!isValidMuteMinutes(durationMinutes)) {
            throw new Error('Mutes run between 5 and 60 minutes.');
        }

        const db = await getDbSafe();
        const roomSnap = await getDoc(doc(db, ROOMS, roomId));
        if (!roomSnap.exists()) throw new Error('This room no longer exists.');
        assertStaff(roomSnap.data(), byUserId, 'mute members');
        if (String(roomSnap.data()?.ownerId) === String(targetUserId)) throw new Error('The host cannot be muted.');

        await updateDoc(doc(db, ROOMS, roomId, 'members', targetUserId), {
            mutedUntil: Timestamp.fromMillis(Date.now() + durationMinutes * 60_000),
            mutedBy: byUserId,
        });

        await writeModLog(db, byUserId, targetUserId, 'room_mute', `${durationMinutes} minutes`, { roomId, durationMinutes });
    },

    async unmuteRoomMember(roomId: string, targetUserId: string, byUserId: string): Promise<void> {
        const db = await getDbSafe();
        const roomSnap = await getDoc(doc(db, ROOMS, roomId));
        if (!roomSnap.exists()) throw new Error('This room no longer exists.');
        assertStaff(roomSnap.data(), byUserId, 'unmute members');
        await updateDoc(doc(db, ROOMS, roomId, 'members', targetUserId), { mutedUntil: null, mutedBy: null });
    },

    /**
     * Kick a member. Permanent for this room instance: the membership document
     * survives with status 'kicked' precisely so a rejoin request can be refused.
     * Any pending request from them is denied in the same breath.
     */
    async kickRoomMember(roomId: string, targetUserId: string, byUserId: string, reason: string): Promise<void> {
        const db = await getDbSafe();
        const roomRef = doc(db, ROOMS, roomId);
        const memberRef = doc(db, ROOMS, roomId, 'members', targetUserId);

        const result = await runTransaction(db, async (tx) => {
            const roomSnap = await tx.get(roomRef);
            if (!roomSnap.exists()) throw new Error('This room no longer exists.');
            const room = roomSnap.data() as any;

            assertStaff(room, byUserId, 'remove members');
            if (String(room.ownerId) === String(targetUserId)) throw new Error('The host cannot be removed.');
            if (String(byUserId) === String(targetUserId)) throw new Error('Use Leave to exit the room.');

            const memberSnap = await tx.get(memberRef);
            if (!memberSnap.exists()) throw new Error('That person is not in the room.');
            const wasActive = memberSnap.data()?.status === 'active';

            const nextCount = wasActive ? Math.max(0, (room.memberCount ?? 1) - 1) : (room.memberCount ?? 0);
            const patch = wasActive
                ? transitionForMemberCount(lifecycleStateOf(room), nextCount, Date.now())
                : {};

            tx.update(memberRef, {
                status: 'kicked',
                kickedAt: serverTimestamp(),
                kickedBy: byUserId,
                role: 'member',
            });
            tx.update(roomRef, {
                members: arrayRemove(targetUserId),
                moderators: arrayRemove(targetUserId),
                memberCount: nextCount,
                lastActivityAt: serverTimestamp(),
                ...patchToFirestore(patch),
            });

            return { graceStarted: (patch as RoomPatch).status === 'grace', room };
        });

        // A pending request from someone just kicked must not sit in the queue
        // looking approvable.
        try {
            const reqRef = doc(db, ROOMS, roomId, 'join_requests', targetUserId);
            const reqSnap = await getDoc(reqRef);
            if (reqSnap.exists() && reqSnap.data()?.status === 'pending') {
                await updateDoc(reqRef, { status: 'denied', decidedBy: byUserId, decidedAt: serverTimestamp() });
            }
        } catch { /* non-critical */ }

        await writeModLog(db, byUserId, targetUserId, 'room_kick', reason, { roomId });

        if (result.graceStarted) {
            await notify(
                staffOf(result.room),
                'ROOM_GRACE_STARTED',
                'Room is running low',
                `${result.room.title || 'Your room'} dropped below 3 members. It closes in 30 minutes unless someone joins.`,
                { roomId, roomTitle: result.room.title || '' },
                roomId,
            );
        }
    },

    // ── Messages ────────────────────────────────────────────────────────────

    /**
     * Post to the room. Same document shape as a DM message, so the existing
     * composer, voice recorder and media uploads carry over unchanged.
     */
    async sendRoomMessage(roomId: string, senderId: string, text: string, additionalData?: any): Promise<void> {
        _checkRoomRateLimit(senderId, 'sendRoomMessage', 1000);

        const db = await getDbSafe();
        const memberSnap = await getDoc(doc(db, ROOMS, roomId, 'members', senderId));
        if (!memberSnap.exists() || memberSnap.data()?.status !== 'active') {
            throw new Error("You're not in this room.");
        }
        const mutedUntil = toMillis(memberSnap.data()?.mutedUntil);
        if (mutedUntil && mutedUntil > Date.now()) {
            const mins = Math.max(1, Math.ceil((mutedUntil - Date.now()) / 60_000));
            throw new Error(`You're muted for another ${mins} minute${mins === 1 ? '' : 's'}.`);
        }

        const roomSnap = await getDoc(doc(db, ROOMS, roomId));
        const status = roomSnap.data()?.status;
        if (!roomSnap.exists() || status === 'closed') throw new Error('This room has ended.');

        const profile = await profileOf(db, senderId);
        await addDoc(collection(db, ROOMS, roomId, 'messages'), {
            senderId,
            senderName: profile.name,
            senderAvatar: profile.avatar,
            text: text || '',
            timestamp: serverTimestamp(),
            ...(additionalData || {}),
        });

        await updateDoc(doc(db, ROOMS, roomId), { lastActivityAt: serverTimestamp() });
    },

    subscribeToRoomMessages(roomId: string, onMessages: (messages: any[]) => void) {
        let unsub: (() => void) | null = null;
        let cancelled = false;
        (async () => {
            const db = await getDbSafe();
            if (cancelled) return;
            const q = query(collection(db, ROOMS, roomId, 'messages'), orderBy('timestamp', 'asc'), limit(500));
            unsub = onSnapshot(q, (snap) => {
                onMessages(snap.docs.map(d => {
                    const data = d.data() as any;
                    return { id: d.id, ...data, timestamp: toMillis(data.timestamp) ?? Date.now() };
                }));
            }, (e) => console.error('[RoomService] messages subscription failed:', e));
        })();
        return () => { cancelled = true; unsub?.(); };
    },

    /**
     * Report a message.
     *
     * Writes to the TOP-LEVEL room_reports collection with the message content
     * copied in, not referenced. The room and its messages are hard-deleted at
     * expiry, so a report holding only a message id would be unreviewable within
     * the day. Reports never auto-act.
     */
    async reportRoomMessage(roomId: string, messageId: string, reporterId: string, reason: string): Promise<void> {
        _checkRoomRateLimit(reporterId, 'reportRoomMessage', 5_000);

        const db = await getDbSafe();
        const [roomSnap, msgSnap] = await Promise.all([
            getDoc(doc(db, ROOMS, roomId)),
            getDoc(doc(db, ROOMS, roomId, 'messages', messageId)),
        ]);
        if (!msgSnap.exists()) throw new Error('That message is gone.');
        const msg = msgSnap.data() as any;

        const roomTitle = roomSnap.exists() ? (roomSnap.data()?.title || '') : '';
        const snapshot = {
            senderId: msg.senderId || '',
            senderName: msg.senderName || '',
            text: msg.text || '',
            imageUrl: msg.imageUrl ?? null,
            audioUrl: msg.audioUrl ?? null,
            videoUrl: msg.videoUrl ?? null,
            sentAt: msg.timestamp ?? null,
        };

        // Both, always, and not one conditional on the other. room_reports is
        // what the room's own mods act on immediately; the app-wide queue is
        // ongoing oversight and must not depend on whether they bothered.
        await addDoc(collection(db, ROOM_REPORTS), {
            roomId,
            roomTitle,
            messageId,
            reporterId,
            reason: (reason || '').trim(),
            status: 'open',
            createdAt: serverTimestamp(),
            snapshot,
        });

        try {
            const { reportToWatchers } = await import('./WatcherService');
            await reportToWatchers(reporterId, messageId, 'room_message', (reason || '').trim(), {
                roomId,
                roomTitle,
                targetUserId: msg.senderId || '',
                reportedUsername: msg.senderName || '',
                // Carried in full so the Watcher panel never has to fetch it:
                // the message is deleted with the room, usually within hours.
                snapshot,
            });
        } catch (e) {
            console.error('[RoomService] Watcher escalation failed:', e);
        }
    },

    /**
     * Report the room itself — its premise, not any one message.
     *
     * Goes straight to the app-wide queue with no room_reports copy: that
     * collection exists so a message outlives the room it was sent in, and a
     * room-level report's snapshot already sits on the report document. There is
     * also no in-room audience for "this room should not exist" — the people who
     * would action it are the ones being reported.
     */
    async reportRoom(roomId: string, reporterId: string, reason: string): Promise<void> {
        _checkRoomRateLimit(reporterId, 'reportRoom', 10_000);

        const db = await getDbSafe();
        const roomSnap = await getDoc(doc(db, ROOMS, roomId));
        if (!roomSnap.exists()) throw new Error('This room no longer exists.');
        const room = roomSnap.data() as any;

        const { reportToWatchers } = await import('./WatcherService');
        await reportToWatchers(reporterId, roomId, 'room', (reason || '').trim(), {
            roomId,
            title: room.title || '',
            rules: room.rules || '',
            ownerId: room.ownerId || '',
            targetUserId: room.ownerId || '',
            reportedUsername: room.ownerName || '',
            snapshot: {
                senderId: room.ownerId || '',
                senderName: room.ownerName || '',
                text: room.title || '',
                sentAt: room.createdAt ?? null,
            },
        });
    },

    /**
     * Force-end a room. Watcher/Legion only, enforced in the rules by an admin
     * branch that permits flipping status to 'closed' and nothing else.
     *
     * No separate purge path: sweepExpiredRooms already collects anything marked
     * closed, so this rides the exact route an owner-ended room takes. Silent by
     * design, matching the existing decision not to notify on moderation actions.
     */
    async forceEndRoom(roomId: string, watcherId: string, watcherUsername: string, reason: string): Promise<void> {
        const db = await getDbSafe();
        const roomSnap = await getDoc(doc(db, ROOMS, roomId));
        if (!roomSnap.exists()) throw new Error('This room is already gone.');
        if (roomSnap.data()?.status === 'closed') throw new Error('This room has already ended.');

        await updateDoc(doc(db, ROOMS, roomId), { status: 'closed' });

        try {
            const { logAuditAction } = await import('./WatcherService');
            await logAuditAction({
                watcher_id: watcherId,
                watcher_username: watcherUsername,
                target_user_id: String(roomSnap.data()?.ownerId || ''),
                action_type: 'force_end_room',
                reason: reason || 'Reported content',
                timestamp: serverTimestamp(),
                metadata: { roomId, roomTitle: roomSnap.data()?.title || '' },
            });
        } catch (e) {
            console.warn('[RoomService] force-end audit log failed (non-critical):', e);
        }
    },

    /**
     * Report a member rather than one message.
     *
     * Apple's UGC rules ask for a way to report abusive USERS, not only
     * individual posts, and the product spec asks for the same. The snapshot
     * records who was reported and from which room, so the report still means
     * something after the room is purged.
     */
    async reportRoomMember(roomId: string, targetUserId: string, reporterId: string, reason: string): Promise<void> {
        _checkRoomRateLimit(reporterId, 'reportRoomMember', 5_000);

        const db = await getDbSafe();
        const [roomSnap, memberSnap] = await Promise.all([
            getDoc(doc(db, ROOMS, roomId)),
            getDoc(doc(db, ROOMS, roomId, 'members', targetUserId)),
        ]);

        const memberRoomTitle = roomSnap.exists() ? (roomSnap.data()?.title || '') : '';
        try {
            const { reportToWatchers } = await import('./WatcherService');
            await reportToWatchers(reporterId, targetUserId, 'user', (reason || '').trim(), {
                targetUserId,
                reportedUsername: memberSnap.exists() ? (memberSnap.data()?.userName || '') : '',
                roomId,
                roomTitle: memberRoomTitle,
                context: 'room',
            });
        } catch (e) {
            console.error('[RoomService] Watcher escalation failed:', e);
        }

        await addDoc(collection(db, ROOM_REPORTS), {
            roomId,
            roomTitle: memberRoomTitle,
            messageId: '',
            reporterId,
            reason: (reason || '').trim(),
            status: 'open',
            createdAt: serverTimestamp(),
            targetType: 'member',
            snapshot: {
                senderId: targetUserId,
                senderName: memberSnap.exists() ? (memberSnap.data()?.userName || '') : '',
                text: '',
                imageUrl: null,
                audioUrl: null,
                videoUrl: null,
                sentAt: null,
            },
        });
    },

    // ── Lifecycle actions ───────────────────────────────────────────────────

    /** Owner-only, title and rules only. */
    async updateRoom(roomId: string, ownerId: string, updates: { title?: string; rules?: string }): Promise<void> {
        const db = await getDbSafe();
        const roomSnap = await getDoc(doc(db, ROOMS, roomId));
        if (!roomSnap.exists()) throw new Error('This room no longer exists.');
        assertOwner(roomSnap.data(), ownerId, 'edit the room');

        const patch: Record<string, any> = {};
        if (updates.title !== undefined) {
            const t = updates.title.trim();
            if (t.length < 3 || t.length > 80) throw new Error('Titles run 3 to 80 characters.');
            patch.title = t;
        }
        if (updates.rules !== undefined) patch.rules = updates.rules.trim();
        if (Object.keys(patch).length === 0) return;

        await updateDoc(doc(db, ROOMS, roomId), patch);
    },

    /** The one-time extension. Capped at 24h from when the room went live. */
    async extendRoom(roomId: string, ownerId: string, additionalMinutes: number): Promise<number> {
        const db = await getDbSafe();
        const roomRef = doc(db, ROOMS, roomId);

        return await runTransaction(db, async (tx) => {
            const roomSnap = await tx.get(roomRef);
            if (!roomSnap.exists()) throw new Error('This room no longer exists.');
            const room = roomSnap.data() as any;

            assertOwner(room, ownerId, 'extend the room');

            const state = lifecycleStateOf(room);
            if (state.status === 'lobby') throw new Error("The room hasn't started yet.");
            if (state.status === 'closed') throw new Error('This room has ended.');
            if (state.extended) throw new Error('This room has already been extended once.');

            const next = extendedExpiresAt(state, additionalMinutes, Date.now());
            if (next == null) throw new Error('This room is already running the maximum 24 hours.');

            tx.update(roomRef, {
                expiresAt: Timestamp.fromMillis(next),
                extended: true,
                // A fresh window earns a fresh warning.
                endingSoonNotifiedAt: null,
            });
            return next;
        });
    },

    /**
     * End early. Marks the room closed; sweepExpiredRooms does the actual purge,
     * since only the Admin SDK can recursively delete the subcollections.
     */
    async endRoom(roomId: string, ownerId: string): Promise<void> {
        const db = await getDbSafe();
        const roomRef = doc(db, ROOMS, roomId);

        await runTransaction(db, async (tx) => {
            const roomSnap = await tx.get(roomRef);
            if (!roomSnap.exists()) return;
            const room = roomSnap.data() as any;
            assertOwner(room, ownerId, 'end the room');
            if (room.status === 'closed') return;

            tx.update(roomRef, { status: 'closed', closedAt: serverTimestamp() });
            tx.update(doc(db, 'users', ownerId), { hostedRoomId: deleteField() });
        });
    },
};

export default RoomService;
